/**
 * Login.js - Login page component
 */

import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { forgotPassword } from '../services/api';
import Logo from './Logo';
import './Auth.css';

function Login({ onSwitchToSignup, onBackToHome }) {
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Forgot password state
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMessage, setForgotMessage] = useState('');
  const [forgotError, setForgotError] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const result = await login(formData.email, formData.password);

    if (!result.success) {
      setError(result.error);
      setLoading(false);
    }
    // Success handled by AuthContext - will update user state
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    setForgotError('');
    setForgotMessage('');

    try {
      const result = await forgotPassword(forgotEmail);
      setForgotMessage(result.message);
    } catch (err) {
      setForgotError(err.message);
    } finally {
      setForgotLoading(false);
    }
  };

  if (showForgot) {
    return (
      <div className="auth-container">
        {onBackToHome && (
          <button className="auth-logo" onClick={onBackToHome} aria-label="Back to home">
            <Logo size="1.6rem" variant="white" />
          </button>
        )}
        <div className="auth-box">
          <h1><Logo size="2.5rem" /></h1>
          <h2>Reset Password</h2>

          {forgotMessage && (
            <div style={{ background: 'var(--color-toast-success-bg)', color: 'var(--color-toast-success-text)', padding: '12px', borderRadius: '8px', marginBottom: '1rem', textAlign: 'center' }}>
              {forgotMessage}
            </div>
          )}
          {forgotError && <div className="error-message">{forgotError}</div>}

          {!forgotMessage && (
            <form onSubmit={handleForgotSubmit}>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => { setForgotEmail(e.target.value); setForgotError(''); }}
                  required
                  placeholder="you@example.com"
                />
              </div>

              <button type="submit" className="auth-button" disabled={forgotLoading}>
                {forgotLoading ? 'Sending...' : 'Send Reset Link'}
              </button>
            </form>
          )}

          <p className="auth-back" style={{ marginTop: '1.5rem' }}>
            <button onClick={() => { setShowForgot(false); setForgotMessage(''); setForgotError(''); }} className="link-button">
              &larr; Back to login
            </button>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      {onBackToHome && (
        <button className="auth-logo" onClick={onBackToHome} aria-label="Back to home">
          <Logo size="1.6rem" variant="white" />
        </button>
      )}
      <div className="auth-box">
        <h1><Logo size="2.5rem" /></h1>
        <h2>Welcome Back</h2>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="you@example.com"
            />
          </div>

          <div className="form-group">
            <label>Password</label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              placeholder="••••••••"
            />
            <div style={{ textAlign: 'right', marginTop: '0.5rem' }}>
              <button
                type="button"
                onClick={() => { setShowForgot(true); setForgotEmail(formData.email); }}
                className="link-button"
                style={{ fontSize: '0.875rem' }}
              >
                Forgot Password?
              </button>
            </div>
          </div>

          <button type="submit" className="auth-button" disabled={loading}>
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>

        <p className="auth-switch">
          Don't have an account?{' '}
          <button onClick={onSwitchToSignup} className="link-button">
            Sign up
          </button>
        </p>
        {onBackToHome && (
          <p className="auth-back">
            <button onClick={onBackToHome} className="link-button">
              &larr; Back to home
            </button>
          </p>
        )}
      </div>
    </div>
  );
}

export default Login;
